import { Avatar, Box, Heading, Text, TooltipCommonProps } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { AIFilled } from '@wix/wix-ui-icons-common';
import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';

import ChevronLeftSmallFilled from '../../../../assets/img/ChevronLeftSmallFilled.svg';
import { BACKGROUND_DARK_COLOR } from '../../../../constants';
import { TLocalesKeys } from '../../../../types';
import { CaroIcon } from '../../../common/components/CaroIcon';
import { useIsAiChatbot } from '../../../common/hooks/useIsAiChatbot';
import { selectIsConnectingAgent, selectIsLiveChat } from '../../../common/selectors/chat';
import { selectOnlineAgent } from '../../../common/selectors/header';
import { AnnouncementBanner } from '../../../components/header/AnnouncementBanner';
import { FloatingBannerOrigin } from '../../../components/header/FloatingBanner/FloatingBanner';
import { StatusBanner } from '../../../components/header/StatusBanner';
import { goBackCopilot } from '../../../messageChannel/postNewMessages';
import { renderModel } from '../../../util/renderModel';
import { headerFullPageDataHooks } from './HeaderFullPage.dataHooks';
import { HeaderFullPageActions } from './HeaderFullPageActions';
import style from './styles.scss';

const titleTooltipProps: TooltipCommonProps = {
  size: 'small',
  placement: 'bottom',
};

const isCopilot = renderModel.isCopilot();
const isEditor3 = renderModel.isEditor3();

export const HeaderFullPage: FC = () => {
  const { t } = useTranslation();
  const isLiveChat = useSelector(selectIsLiveChat);
  const agent = useSelector(selectOnlineAgent);
  const isAiChatbot = useIsAiChatbot();
  const isConnectingAgent = useSelector(selectIsConnectingAgent);

  const titleSection = useMemo(() => {
    const shouldShowAgentInfo = isLiveChat && agent;

    if (isAiChatbot) {
      return (
        <Box direction="vertical" gap="0.5">
          <Text size="tiny" weight="thin" light dataHook={headerFullPageDataHooks.title()}>
            {t<TLocalesKeys>('subheader_title')}
          </Text>
          {shouldShowAgentInfo ? (
            <Box gap="0.5" verticalAlign="middle">
              <Avatar
                size="size24"
                shape="square"
                name={agent.fullName}
                imgProps={{ src: agent.profileImage }}
                dataHook={headerFullPageDataHooks.agentAvatar()}
              />

              <Text light weight="bold" ellipsis tooltipProps={titleTooltipProps}>
                {agent.fullName}
              </Text>
            </Box>
          ) : (
            <Box gap="0.5" verticalAlign="middle">
              {isConnectingAgent ? (
                <Text light weight="bold">
                  {t<TLocalesKeys>('header_title_connecting_agent')}
                </Text>
              ) : (
                <>
                  <Text light>
                    <Box>
                      <AIFilled />
                    </Box>
                  </Text>
                  <Text light weight="bold">
                    {t<TLocalesKeys>('widget_title_ai_chatbot')}
                  </Text>
                </>
              )}
            </Box>
          )}
        </Box>
      );
    }

    return (
      <Box verticalAlign="middle" gap={2} flexShrink={1} overflow="hidden">
        {shouldShowAgentInfo ? (
          <Avatar
            size="size36"
            name={agent.fullName}
            imgProps={{ src: agent.profileImage }}
            dataHook={headerFullPageDataHooks.agentAvatar()}
          />
        ) : (
          <CaroIcon size={36} />
        )}
        <Box direction="vertical" overflow="hidden">
          <Text weight="bold" light dataHook={headerFullPageDataHooks.title()}>
            {t<TLocalesKeys>('subheader_title')}
          </Text>
          <Text
            size="small"
            light
            secondary
            ellipsis
            tooltipProps={titleTooltipProps}
            dataHook={headerFullPageDataHooks.secondaryTitle()}
          >
            {isLiveChat && agent ? agent.fullName : t<TLocalesKeys>('widget_title')}
          </Text>
        </Box>
      </Box>
    );
  }, [t, isLiveChat, agent, isAiChatbot, isConnectingAgent]);

  if (isCopilot) {
    return (
      <>
        <Box className={style.copilotAppHeader} align="space-between" paddingRight="SP2" marginBottom="SP2">
          <Box className={style.copilotAppHeaderTitleWrapper} gap="8px">
            <ChevronLeftSmallFilled onClick={goBackCopilot} className={style.chevronLeft} />
            <Heading
              className={style.copilotAppHeaderTitle}
              size="medium"
              dataHook={headerFullPageDataHooks.title()}
            >
              {t<TLocalesKeys>('widget_title_ai_chatbot')}
            </Heading>
          </Box>
          <HeaderFullPageActions />
        </Box>
        <AnnouncementBanner dataHook={headerFullPageDataHooks.announcementBanner()} />
        <StatusBanner />
      </>
    );
  }

  return (
    <>
      <Box
        className={style.contactAppHeader}
        align="space-between"
        verticalAlign="middle"
        backgroundColor={BACKGROUND_DARK_COLOR}
      >
        {titleSection}
        <HeaderFullPageActions />
      </Box>
      <AnnouncementBanner dataHook={headerFullPageDataHooks.announcementBanner()} />
      <StatusBanner origin={FloatingBannerOrigin.FullPage} />
    </>
  );
};
